import React, { useState, useEffect } from 'react';
import './OpportuniteDetailPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { GraduationIcon, BriefcaseIcon, DocumentIcon, InfoIcon } from '../components/Icons';
import opportuniteService from '../services/opportuniteService';
import { getImageUrl, handleImageError } from '../utils/imageUtils';
import { toSentenceCase } from '../utils/textUtils';
import useSEO from '../hooks/useSEO';

function OpportuniteDetailPage({ slug }) {
  const [opportunite, setOpportunite] = useState(null);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: opportunite?.titre ? `${opportunite.titre} | Opportunités` : "Détail de l'opportunité",
    description: opportunite?.description ? opportunite.description.substring(0, 160) : "Découvrez les détails de cette opportunité au Groupe Scolaire et Universitaire Les Palmiers.",
    keywords: "offre de stage Bénin, bourse étude Togba, opportunité académique Calavi, emploi étudiant Bénin",
    canonicalUrl: typeof window !== 'undefined' ? window.location.href : 'https://gsulespalmiers.bj/opportunites',
    ogImage: opportunite?.image ? getImageUrl(opportunite.image) : 'https://gsulespalmiers.bj/images/logo-gsu.png'
  });

  useEffect(() => {
    loadOpportunite();
  }, [slug]);

  const loadOpportunite = async () => {
    try {
      setLoading(true);
      const response = await opportuniteService.getBySlug(slug);
      setOpportunite(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'opportunité:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="opportunite-detail-page">
        <Header />
        <div className="container" style={{padding: '4rem 0', textAlign: 'center'}}>
          <p>Chargement...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!opportunite) {
    return (
      <div className="opportunite-detail-page">
        <Header />
        <div className="container" style={{padding: '4rem 0', textAlign: 'center'}}>
          <p>Opportunité non trouvée</p>
          <button 
            className="btn btn-primary"
            onClick={() => window.navigateTo && window.navigateTo('opportunites')}
          >
            Retour aux opportunités
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="opportunite-detail-page">
      <Header />
      
      <div className="opportunite-detail-hero">
        <div className="container">
          <button 
            className="back-btn"
            onClick={() => window.navigateTo && window.navigateTo('opportunites')}
          >
            ← Retour
          </button>
          <span className="type-badge">
            {opportunite.type === 'bourse' ? (
              <><GraduationIcon size={16} color="#fff" /> Bourse</>
            ) : (
              <><BriefcaseIcon size={16} color="#fff" /> Stage</>
            )}
          </span>
          <h1>{toSentenceCase(opportunite.titre)}</h1>
          <div className="meta-info">
            <span className="organisme">🏢 {toSentenceCase(opportunite.organisme)}</span>
            <span className="location">📍 {opportunite.ville ? `${toSentenceCase(opportunite.ville)}, ` : ''}{toSentenceCase(opportunite.pays)}</span>
            <span className="date-limite">⏰ Date limite: {new Date(opportunite.date_limite).toLocaleDateString('fr-FR')}</span>
          </div>
        </div>
      </div>

      <div className="opportunite-detail-content">
        <div className="container">
          <div className="content-grid">
            <div className="main-content">
              {opportunite.image && (
                <div className="opportunite-image-large">
                  <img 
                    src={getImageUrl(opportunite.image, '/images/logo.svg')} 
                    alt={opportunite.titre}
                    onError={(e) => handleImageError(e, '/images/logo.svg')}
                  />
                </div>
              )}

              <section className="detail-section">
                <h2><DocumentIcon size={24} color="#0078D7" /> Description</h2>
                <p className="description-text">{toSentenceCase(opportunite.description)}</p>
              </section>

              {opportunite.contenu && (
                <section className="detail-section">
                  <h2><DocumentIcon size={24} color="#0078D7" /> Détails complets</h2>
                  <div className="contenu-text">{toSentenceCase(opportunite.contenu)}</div>
                </section>
              )}

              {opportunite.criteres && opportunite.criteres.length > 0 && (
                <section className="detail-section">
                  <h2>✅ Critères d'éligibilité</h2>
                  <ul className="criteres-list">
                    {opportunite.criteres.map((critere, index) => (
                      <li key={index}>{toSentenceCase(critere)}</li>
                    ))}
                  </ul>
                </section>
              )}

              {opportunite.documents_requis && opportunite.documents_requis.length > 0 && (
                <section className="detail-section">
                  <h2><DocumentIcon size={24} /> Documents requis</h2>
                  <ul className="documents-list">
                    {opportunite.documents_requis.map((doc, index) => (
                      <li key={index}>{toSentenceCase(doc)}</li>
                    ))}
                  </ul>
                </section>
              )}
            </div>

            <div className="sidebar">
              <div className="action-card">
                <h3>{opportunite.type === 'bourse' ? 'Postuler à cette bourse' : 'Postuler à ce stage'}</h3>
                
                {opportunite.lien_candidature ? (
                  <>
                    <p className="action-description">
                      Cliquez sur le bouton ci-dessous pour accéder au formulaire de candidature officiel.
                    </p>
                    <a 
                      href={opportunite.lien_candidature}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary btn-full"
                    >
                      {opportunite.type === 'bourse' ? '🎓 Postuler maintenant' : '💼 Postuler au stage'}
                    </a>
                  </>
                ) : (
                  <p className="no-link">
                    Veuillez contacter directement {opportunite.organisme} pour plus d'informations sur la candidature.
                  </p>
                )}

                <div className="info-box">
                  <h4><InfoIcon size={20} color="#0078D7" /> Informations importantes</h4>
                  <ul>
                    <li><strong>Organisme:</strong> {toSentenceCase(opportunite.organisme)}</li>
                    <li><strong>Pays:</strong> {toSentenceCase(opportunite.pays)}</li>
                    {opportunite.ville && <li><strong>Ville:</strong> {toSentenceCase(opportunite.ville)}</li>}
                    <li><strong>Date limite:</strong> {new Date(opportunite.date_limite).toLocaleDateString('fr-FR', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}</li>
                  </ul>
                </div>

                <div className="share-section">
                  <h4>📢 Partager cette opportunité</h4>
                  <div className="share-buttons">
                    <button className="share-btn facebook">Facebook</button>
                    <button className="share-btn twitter">Twitter</button>
                    <button className="share-btn whatsapp">WhatsApp</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}

export default OpportuniteDetailPage;
